import React from 'react';
import { Autocomplete, Grid, Paper, PaperProps, TextField, Typography } from '@mui/material';
import { useFormikContext } from 'formik';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { PhoneSuggestionData } from '../../../../../api';
import { EditOrderFormValues } from '../../EditOrderDialog';
import { usePhoneSuggestions } from './usePhoneSuggestions';

type PhoneSuggestFieldProps = {
  onSuggestionPicked: (suggestion: PhoneSuggestionData) => void;
};

const LAST_ORDER_DATE_FORMAT = 'DD.MM.YY';

const SuggestionsPaper = ({ children, ...paperProps }: PaperProps) => {
  const { t } = useTranslation();
  return (
    <Paper {...paperProps}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 2, pt: 1, pb: 0.5 }}>
        {t('calendar.create_order.fillFromPastOrder')}
      </Typography>
      {children}
    </Paper>
  );
};

export const PhoneSuggestField = ({ onSuggestionPicked }: PhoneSuggestFieldProps) => {
  const { t } = useTranslation();
  const { values, errors, touched, setFieldValue, setFieldTouched } = useFormikContext<EditOrderFormValues>();
  const { suggestions } = usePhoneSuggestions(values.phone);

  const pickSuggestion = (suggestion: PhoneSuggestionData) => {
    void setFieldValue('phone', suggestion.phone);
    void setFieldValue('firstname', suggestion.firstname);
    void setFieldValue('lastname', suggestion.lastname);
    void setFieldValue('email', suggestion.email);
    onSuggestionPicked(suggestion);
  };

  return (
    <Autocomplete
      freeSolo
      fullWidth
      options={suggestions}
      filterOptions={(options) => options}
      inputValue={values.phone}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.phone)}
      isOptionEqualToValue={(option, value) => option.phone === value.phone}
      onInputChange={(_event, newInputValue, reason) => {
        if (reason === 'input' || reason === 'clear') {
          void setFieldValue('phone', newInputValue);
        }
      }}
      onChange={(_event, selected) => {
        if (selected && typeof selected !== 'string') {
          pickSuggestion(selected);
        }
      }}
      PaperComponent={SuggestionsPaper}
      renderOption={(optionProps, option) => (
        <li {...optionProps} key={`${option.phone}-${option.firstname}-${option.lastname}`}>
          <Grid container flexDirection="column">
            <Typography variant="body2">{`${option.firstname} ${option.lastname}`.trim()}</Typography>
            <Typography variant="caption" color="text.secondary">
              <span dir="ltr">{option.phone}</span>
              {option.last_order_date &&
                ` · ${t('calendar.create_order.lastOrderOn', {
                  date: moment(option.last_order_date).format(LAST_ORDER_DATE_FORMAT),
                })}`}
            </Typography>
          </Grid>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          className="text-field"
          id="phone"
          name="phone"
          type="tel"
          label={t('order_page.phone')}
          inputProps={{ ...params.inputProps, dir: 'ltr' }}
          onBlur={() => {
            void setFieldTouched('phone', true);
          }}
          error={touched.phone && Boolean(errors.phone)}
          helperText={touched.phone && errors.phone}
        />
      )}
    />
  );
};
